"use client";

import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const projects = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_residential_complex.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_residential_complex.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_residential_complex.png-wvc-sizes",
  alt: "Modern residential complex in Yerevan",
  category: "RESIDENTIAL",
  id: "01",
  title: "Northern Avenue Heights"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_office_interior.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_office_interior.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_office_interior.webp-wvc-sizes",
  alt: "Contemporary office space design",
  category: "COMMERCIAL",
  id: "02",
  title: "Tech Hub Headquarters"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_luxury_villa.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_luxury_villa.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_luxury_villa.png-wvc-sizes",
  alt: "Minimalist luxury interior",
  category: "INTERIOR",
  id: "03",
  title: "Cascade Minimalist Villa"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_historic_renovation.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_historic_renovation.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_historic_renovation.png-wvc-sizes",
  alt: "Historic building renovation project",
  category: "RENOVATION",
  id: "04",
  title: "Old Yerevan Restoration"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_turnkey_project.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_turnkey_project.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_turnkey_project.png-wvc-sizes",
  alt: "Full turnkey architectural solution",
  category: "TURNKEY",
  id: "05",
  title: "Dilijan Forest Retreat"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_urban_planning.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_urban_planning.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/archiforma_urban_planning.png-wvc-sizes",
  alt: "Urban planning and development",
  category: "PLANNING",
  id: "06",
  title: "Sustainable District Masterplan"
}];
function ProjectCard({
  project,
  index
}: {
  project: typeof projects[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div data-index={index} ref={ref} className={`group relative overflow-hidden transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`} style={{
    transitionDelay: `${index % 3 * 150}ms`
  }}>
      <Dialog>
        <DialogTrigger asChild>
          <div className="cursor-pointer">
            <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-muted">
              <img src={project.src} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-black/20 opacity-0 transition-opacity duration-500 group-hover:opacity-100 flex items-center justify-center">
                <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center text-white shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <Plus className="size-6" />
                </div>
              </div>
              <div className="absolute top-6 left-6">
                <span className="font-mono text-[10px] tracking-[0.2em] text-white bg-black/40 backdrop-blur-md px-3 py-1 rounded-sm">
                  {project.category} {project.id}
                </span>
              </div>
            </div>
            <div className="mt-6 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-foreground tracking-tight group-hover:text-primary transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-sm text-muted-foreground font-mono mt-1 uppercase tracking-wider">
                  {project.category}
                </p>
              </div>
              <div className="h-[1px] w-8 bg-border mt-3 group-hover:w-12 group-hover:bg-primary transition-all duration-500" />
            </div>
          </div>
        </DialogTrigger>
        <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden border-border/50 bg-background/95 backdrop-blur-xl rounded-lg shadow-2xl">
          <div className="grid md:grid-cols-12 h-full max-h-[90vh]">
            <div className="md:col-span-8 bg-muted flex items-center justify-center overflow-hidden">
              <img src={project.src} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} className="w-full h-full object-contain" />
            </div>
            <div className="md:col-span-4 p-8 md:p-12 flex flex-col justify-center">
              <span className="font-mono text-xs tracking-[0.3em] text-primary mb-4">
                PROJECT {project.id}
              </span>
              <h2 className="text-3xl font-bold text-foreground mb-6 tracking-tight">
                {project.title}
              </h2>
              <div className="space-y-6 text-muted-foreground">
                <p className="leading-relaxed">
                  A testament to ArchiForma's commitment to structural honesty and modern aesthetics. This project exemplifies our turnkey approach, blending technical precision with creative vision.
                </p>
                <div className="pt-6 border-t border-border">
                  <div className="grid grid-cols-2 gap-4 text-[11px] font-mono tracking-widest uppercase">
                    <div>
                      <p className="text-foreground/50 mb-1">Category</p>
                      <p className="text-foreground">{project.category}</p>
                    </div>
                    <div>
                      <p className="text-foreground/50 mb-1">Year</p>
                      <p className="text-foreground">2023</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}
export default function LandmarkProjects() {
  return <section data-section-id="257193" id="landmark-projects" className="bg-background py-24 md:py-32 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-12 mb-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-[1px] w-12 bg-primary" />
              <span className="font-mono text-[10px] tracking-[0.4em] text-primary uppercase">
                Portfolio Selection
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-[1.1] mb-8">
              Landmark <span className="text-primary">Achievements</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
              A curated exhibit of our most significant architectural milestones. From residential sanctuaries to commercial hubs, we define the modern landscape with technical precision.
            </p>
          </div>
          
          <div className="flex items-center">
            <Link to="/HomePage" className="group flex items-center gap-4 text-foreground font-bold text-xs tracking-[0.2em] uppercase transition-colors hover:text-primary">
              <span>View Full Archive</span>
              <div className="relative flex items-center justify-center h-10 w-10 rounded-lg border border-border group-hover:border-primary group-hover:bg-primary group-hover:text-white transition-all duration-300">
                <ArrowRight className="size-4" />
              </div>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {projects.map((project, index) => <ProjectCard key={index} project={project} index={index} />)}
        </div>

        <div className="mt-24 pt-12 border-t border-border flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-8">
            <div className="text-center md:text-left">
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase mb-1">Completed Projects</p>
              <p className="text-2xl font-bold text-foreground">150+</p>
            </div>
            <div className="h-8 w-[1px] bg-border hidden md:block" />
            <div className="text-center md:text-left">
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase mb-1">Design Awards</p>
              <p className="text-2xl font-bold text-foreground">12</p>
            </div>
          </div>
          <p className="text-xs font-mono text-muted-foreground tracking-widest uppercase">
            © 2007 — 2024 ArchiForma Studio
          </p>
        </div>
      </div>
    </section>;
}